"use client";

import Link from "next/link";
import type { BrowseMode } from "@/lib/browse/getHomeBrowseData";
import type { EditorialCategorySelection } from "@/lib/homepage/editorial";
import { useLocation } from "@/components/location/LocationProvider";
import { withLocationHref } from "@/lib/location";
import EditorialListingCard from "./EditorialListingCard";
import LazyAmbientVideo from "./LazyAmbientVideo";
import styles from "./HomepageEditorial.module.css";

export default function EditorialCategoryBand({
  category,
  mode,
  tone,
}: {
  category: EditorialCategorySelection;
  mode: BrowseMode;
  tone: "cream" | "rose" | "ivory";
}) {
  const { location } = useLocation();
  const ctaHref = withLocationHref(category.href, location);
  const mediaRight = category.mediaSide === "right";
  const selectedCount = category.listings.length;
  const inventorySignal =
    selectedCount === 1
      ? "1 local find available"
      : selectedCount > 1
        ? `${selectedCount} selected finds nearby`
        : null;

  return (
    <section
      className={`${styles.band} ${styles[tone]}`}
      data-testid={`editorial-band-${category.id}`}
      data-category-fallback={category.usedFallback ? "true" : "false"}
      data-section-tone={tone}
    >
      <div
        className={`${styles.bandInner} ${
          mediaRight ? styles.mediaRight : ""
        }`}
      >
        <LazyAmbientVideo
          src={category.videoSrc}
          label={category.id}
          className={styles.videoFrame}
        />

        <div
          className={styles.bandContent}
          data-has-listings={selectedCount > 0 ? "true" : "false"}
        >
          <p className={styles.eyebrow}>{category.eyebrow}</p>
          <h2 className={styles.heading}>{category.heading}</h2>
          <p className={styles.description}>{category.description}</p>

          {inventorySignal ? (
            <p
              className={styles.inventorySignal}
              data-testid={`editorial-inventory-${category.id}`}
            >
              {inventorySignal}
            </p>
          ) : null}

          {selectedCount > 0 ? (
            <div
              className={`${styles.listingGroup} ${
                selectedCount === 1
                  ? styles.listingGroupSingle
                  : styles.listingGroupMultiple
              }`}
              aria-label={`${category.eyebrow} listings`}
              data-testid={`editorial-listings-${category.id}`}
              data-listing-count={selectedCount}
            >
              {category.listings.map((listing) => (
                <EditorialListingCard
                  key={listing.public_id || listing.id}
                  listing={listing}
                  mode={mode}
                />
              ))}
            </div>
          ) : null}

          <Link href={ctaHref} prefetch={false} className={styles.textCta}>
            <span className={styles.ctaLabel}>{category.ctaLabel}</span>
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
